import { SocialMediaPost, SocialPostStatus } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { Edit, Trash2, Clock, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const STATUS_STYLES: Record<SocialPostStatus, string> = {
  draft: 'bg-muted text-muted-foreground',
  scheduled: 'bg-blue-100 text-blue-700',
  publishing: 'bg-yellow-100 text-yellow-700',
  published: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
  cancelled: 'bg-gray-100 text-gray-600',
};

const STATUS_ICONS: Record<SocialPostStatus, React.ReactNode> = {
  draft: null,
  scheduled: <Clock className="h-3 w-3" />,
  publishing: <Loader2 className="h-3 w-3 animate-spin" />,
  published: <CheckCircle className="h-3 w-3" />,
  failed: <XCircle className="h-3 w-3" />,
  cancelled: null,
};

interface PostCardProps {
  post: SocialMediaPost;
  onDelete?: (postId: string) => void;
}

export function PostCard({ post, onDelete }: PostCardProps) {
  const navigate = useNavigate();
  const statusStyle = STATUS_STYLES[post.status];
  const statusIcon = STATUS_ICONS[post.status];
  const preview = post.originalContent.slice(0, 150);
  const isPreviewTruncated = post.originalContent.length > 150;

  return (
    <div className="p-4 border rounded-lg bg-card space-y-3">
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm text-foreground line-clamp-3">
          {preview}
          {isPreviewTruncated && '...'}
        </p>
        <div className="flex items-center gap-1 shrink-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/a/arche_social_media/compose/${post.postId}`)}
          >
            <Edit className="h-4 w-4" />
            <span className="sr-only">Edit</span>
          </Button>
          {onDelete && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDelete(post.postId)}
              className="text-muted-foreground hover:text-destructive"
            >
              <Trash2 className="h-4 w-4" />
              <span className="sr-only">Delete</span>
            </Button>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between">
        <span
          className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${statusStyle}`}
        >
          {statusIcon}
          {post.status}
        </span>
        <span className="text-xs text-muted-foreground">
          {post.publishAt
            ? `Scheduled ${formatDistanceToNow(new Date(post.publishAt), { addSuffix: true })}`
            : `Created ${formatDistanceToNow(new Date(post.createdAt), { addSuffix: true })}`}
        </span>
      </div>

      {post.targetAccountIds.length > 0 && (
        <p className="text-xs text-muted-foreground">
          {post.targetAccountIds.length} account{post.targetAccountIds.length > 1 ? 's' : ''}{' '}
          targeted
        </p>
      )}
    </div>
  );
}
